import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchItem } from "@/lib/api";
import { CATEGORY_BY_TYPE } from "@/lib/categories";
import { resolveImage, onImgError, normalizeSrc } from "@/lib/images";
import AudioPlayer from "@/components/AudioPlayer";
import SourcesBlock from "@/components/SourcesBlock";
import RichText from "@/components/RichText";
import Seo from "@/components/Seo";
import { articleLd, breadcrumbLd, graph } from "@/lib/jsonld";
import ArticleLangScope from "@/components/ArticleLangScope";
import BookmarkButton from "@/components/BookmarkButton";
import KnowledgeCard from "@/components/KnowledgeCard";
import RelatedServices from "@/components/RelatedServices";
import PageEngage from "@/components/PageEngage";
import { Divider } from "@/components/MotifBackground";
import { ChevronRight, ChevronLeft, Share2, Clock, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import ReaderMode from "@/components/ReaderMode";
import ReadingProgress from "@/components/ReadingProgress";
import { fireGamify } from "@/lib/gamify";

export default function KathaReader() {
  return (
    <ArticleLangScope testid="katha">
      <KathaReaderInner />
    </ArticleLangScope>
  );
}

function KathaReaderInner() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang, pick, pickTitle, pickSecondary, pickDesc, isDeva } = useLang();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [chapter, setChapter] = useState(0);
  const [readerOpen, setReaderOpen] = useState(false);
  const chapterRef = useRef(null);
  const chapterInit = useRef(true);

  // Scroll to the top of the chapter when navigating chapters (not on first load).
  useEffect(() => {
    if (chapterInit.current) { chapterInit.current = false; return; }
    chapterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [chapter]);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    setChapter(0);
    fetchItem(slug).then((data) => {
      if (data.slug && data.slug !== slug) {
        navigate(location.pathname.replace(/[^/]+$/, data.slug), { replace: true });
        return;
      }
      setItem(data);
      fireGamify("read", data.slug || slug);
    }).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  const share = async () => {
    const url = `${window.location.origin}/api/share${window.location.pathname}`;
    const shareTitle = pickTitle(item);
    try {
      if (navigator.share) await navigator.share({ title: shareTitle, url });
      else {
        await navigator.clipboard.writeText(url);
        toast("Link copied", { description: shareTitle });
      }
    } catch { /* cancelled */ }
  };

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Not found.</T></div>;
  if (!item) return <OmSpinner />;

  const cat = CATEGORY_BY_TYPE[item.type];
  const catDeva = cat?.deva || "कथा";
  const catEn = cat?.en || "Katha";
  const chapters = item.chapters || [];
  const current = chapters[chapter];
  const body = current ? (lang === "hi" ? current.body_hi || current.body_en : current.body_en || current.body_hi) : "";
  const chL = (en, hi) => (lang === "hi" ? (hi || en) : (en || hi)) || "";
  const L = (f) => (f && typeof f === "object") ? (lang === "hi" ? (f.hi || f.en) : (f.en || f.hi)) : (f || "");
  const introText = L(item.intro);
  const overviewText = L(item.body);
  const hasNarrative = introText || overviewText;
  const title = pickTitle(item);
  // Secondary (companion) title — always show the "other" language authored title
  // so both readings are honoured (e.g. English title beneath Hindi heading).
  const secondaryTitle = pickSecondary(item);
  const shortDesc = pickDesc(item);
  const bannerSrc = normalizeSrc(item.image_url);
  const chapterTitle = current ? (lang === "hi"
    ? (current.devanagari_title || current.title)
    : (current.title || current.devanagari_title)) : "";
  const chapterSecondary = current && current.title && current.devanagari_title && chapterTitle !== (lang === "hi" ? current.title : current.devanagari_title)
    ? (lang === "hi" ? current.title : current.devanagari_title)
    : "";

  return (
    <article className="pb-24">
      <Seo
        path={`${cat?.path || "/kathas"}/${item.slug}`}
        title={item.seo_title || title}
        description={item.seo_description || shortDesc}
        keywords={item.seo_keywords}
        image={item.preview_image || item.image_url}
        type="article"
        jsonLd={graph(
          articleLd({ title: item.seo_title || title, description: item.seo_description || shortDesc, image: item.preview_image || item.image_url, path: `${cat?.path || "/kathas"}/${item.slug}`, datePublished: item.created_at, dateModified: item.updated_at }),
          breadcrumbLd([{ name: "Dharmartha", path: "/" }, { name: catEn || "Kathas", path: cat?.path || "/kathas" }, { name: title, path: `${cat?.path || "/kathas"}/${item.slug}` }])
        )}
      />
      <ReadingProgress />
      {item.image_url && (
        <div className="relative h-[42vh] min-h-[260px] w-full overflow-hidden border-b border-line bg-[var(--parchment)]">
          {/* Blurred cover fill so any aspect ratio looks intentional… */}
          <img src={bannerSrc} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-2xl" />
          {/* …with the full image shown, uncropped, centered on top. */}
          <img src={bannerSrc} alt={title} onError={onImgError(item)} className="absolute inset-0 mx-auto h-full w-auto max-w-full object-contain" />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(20,15,10,0.78)] via-[rgba(20,15,10,0.20)] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-w-5xl px-5 pb-6 md:px-8">
            <p className="overline-label text-[var(--gold)]">{pick(catDeva, catEn)} · {item.category}</p>
            <h1 className={`mt-2 ${isDeva ? "font-deva" : "font-serif-latin"} text-4xl leading-tight text-white deva-lh md:text-6xl`}>{title}</h1>
          </div>
        </div>
      )}
      <header className="border-b border-line bg-[var(--surface)]">
        <div className="mx-auto max-w-5xl px-5 py-10 md:px-8 md:py-14">
          <nav className="mb-6 flex items-center gap-1.5 text-xs text-ink-3">
            <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={cat?.path || "/kathas"} className="hover:text-saffron">{catEn}</Link>
          </nav>
          {!item.image_url && (
            <>
              <p className="overline-label text-gold">{pick(catDeva, catEn)} · {item.category}</p>
              <h1 className={`mt-3 ${isDeva ? "font-deva" : "font-serif-latin"} text-4xl leading-tight text-ink deva-lh md:text-6xl`}>{title}</h1>
            </>
          )}
          {secondaryTitle && (
            <p className={`mt-2 text-xl italic text-ink-2 ${lang === "hi" ? "font-serif-latin" : "font-deva"}`}>{secondaryTitle}</p>
          )}
          {shortDesc && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-2">{shortDesc}</p>}
          <div className="mt-5 flex items-center">
            <span className="h-px w-10 bg-[var(--gold-muted)]" />
            <span className="mx-3 h-1.5 w-1.5 rotate-45 bg-[var(--gold)]" />
            <span className="h-px w-20 bg-[var(--gold-muted)]" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BookmarkButton slug={item.slug} title={title} />
            <button onClick={share} data-testid="share-button" className="inline-flex items-center gap-2 border border-line px-4 py-2.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
              <Share2 className="h-4 w-4" /> {t("share")}
            </button>
            {item.read_time && <span className="inline-flex items-center gap-1.5 text-sm text-ink-3"><Clock className="h-4 w-4" /> {item.read_time}</span>}
          </div>
        </div>
      </header>

      <div className="relative z-[2] mx-auto grid max-w-5xl gap-12 border-t border-line px-5 py-12 md:grid-cols-12 md:px-8 md:py-16">
        {/* Chapter nav */}
        {chapters.length > 0 && (
          <aside className="min-w-0 md:col-span-4">
            <div className="md:sticky md:top-24">
              <p className="overline-label mb-4 text-ink-3">{pick("अध्याय", "Chapters")}</p>
              <ol className="space-y-1">
                {chapters.map((ch, i) => {
                  const chTitle = lang === "hi"
                    ? (ch.devanagari_title || ch.title)
                    : (ch.title || ch.devanagari_title);
                  return (
                    <li key={i}>
                      <button
                        data-testid={`chapter-nav-${i}`}
                        onClick={() => setChapter(i)}
                        className={`flex w-full items-baseline gap-3 border-l-2 py-2.5 pl-4 text-left transition-colors ${
                          i === chapter ? "border-saffron text-ink" : "border-line text-ink-3 hover:text-ink-2"
                        }`}
                      >
                        <span className="shrink-0 font-serif-latin text-sm">{String(i + 1).padStart(2, "0")}</span>
                        <span className={`min-w-0 flex-1 break-words text-[15px] leading-snug ${lang === "en" ? "font-serif-latin" : "font-deva-body"}`}>{chTitle}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <div className="mt-8">
                <AudioPlayer label={`${title} — ${pick("पाठ", "narration")}`} src={item.audio_url} available={!!item.audio_url} />
              </div>
            </div>
          </aside>
        )}

        {/* Chapter body */}
        <div className={chapters.length > 0 ? "min-w-0 md:col-span-8" : "min-w-0 md:col-span-12"}>
          {/* Intro + Overview narrative (rendered when the katha isn't split into chapters, or alongside them) */}
          {hasNarrative && (
            <div className="fade-up mb-12">
              {introText && (
                <RichText value={introText} className="reading-content journal-body max-w-[65ch] font-serif-latin text-lg leading-[1.95] md:text-xl" />
              )}
              {overviewText && (
                <RichText value={overviewText} className={`reading-content ${introText ? "mt-8" : "journal-body"} max-w-[65ch] font-serif-latin text-[17px] leading-[1.95] md:text-lg`} />
              )}
            </div>
          )}

          {current && (
            <div className="fade-up scroll-mt-24" key={chapter} ref={chapterRef}>
              <p className="overline-label text-gold">{pick("अध्याय", "Chapter")} {chapter + 1}</p>
              <h2 className={`mt-2 text-3xl text-ink deva-lh ${lang === "en" ? "font-serif-latin" : "font-deva"}`}>{chapterTitle}</h2>
              {chapterSecondary && (
                <p className={`mt-1 text-lg text-ink-2 ${lang === "hi" ? "font-serif-latin" : "font-deva"}`}>{chapterSecondary}</p>
              )}
              <button
                data-testid="open-reader-btn"
                onClick={() => setReaderOpen(true)}
                className="mt-4 inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"
              >
                <Maximize2 className="h-4 w-4" /> {pick("पूर्ण-स्क्रीन पाठ", "Focus reader")}
              </button>
              <div className="reading-content mt-6 whitespace-pre-line break-words font-deva-body text-[19px] leading-[2] text-ink-2 first-letter:float-left first-letter:mr-3 first-letter:mt-1.5 first-letter:font-deva first-letter:text-6xl first-letter:leading-[0.8] first-letter:text-saffron">
                {body}
              </div>

              {/* Scripture chapters (Durga Saptashati, Rudram, Gita…): Sanskrit verses,
                  transliteration, meaning, context, benefits & purpose per chapter. */}
              {current.sanskrit_text && (
                <div className="mt-8 border border-line bg-parchment/40 p-5 md:p-6">
                  <p className="overline-label mb-3 text-gold">{pick("मूल पाठ", "Sanskrit Text")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line font-deva-body text-[22px] leading-[2.1] text-ink" data-testid="chapter-sanskrit">{current.sanskrit_text}</div>
                </div>
              )}
              {current.transliteration && (
                <div className="mt-6">
                  <p className="overline-label mb-2 text-ink-3">{pick("लिप्यंतरण", "Transliteration")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line text-lg italic leading-[1.9] text-ink-2" data-testid="chapter-translit">{current.transliteration}</div>
                </div>
              )}
              {chL(current.meaning_en, current.meaning_hi) && (
                <div className="mt-6">
                  <p className="overline-label mb-2 text-gold">{pick("अर्थ", "Meaning")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line text-lg leading-[1.9] text-ink-2" data-testid="chapter-meaning">{chL(current.meaning_en, current.meaning_hi)}</div>
                </div>
              )}
              {chL(current.context_en, current.context_hi) && (
                <div className="mt-6">
                  <p className="overline-label mb-2 text-gold">{pick("संदर्भ", "Context")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line leading-[1.95] text-ink-2">{chL(current.context_en, current.context_hi)}</div>
                </div>
              )}
              {chL(current.benefits_en, current.benefits_hi) && (
                <div className="mt-6">
                  <p className="overline-label mb-2 text-gold">{pick("लाभ", "Benefits")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line leading-[1.95] text-ink-2">{chL(current.benefits_en, current.benefits_hi)}</div>
                </div>
              )}
              {chL(current.purpose_en, current.purpose_hi) && (
                <div className="mt-6">
                  <p className="overline-label mb-2 text-gold">{pick("प्रयोजन", "Purpose")}</p>
                  <div className="reading-content wrap-anywhere whitespace-pre-line leading-[1.95] text-ink-2">{chL(current.purpose_en, current.purpose_hi)}</div>
                </div>
              )}
            </div>
          )}

          {/* Prev / Next */}
          {chapters.length > 1 && (
            <div className="mt-12 flex items-center justify-between border-t border-line pt-6">
              <button
                data-testid="katha-prev-chapter-btn"
                disabled={chapter === 0}
                onClick={() => setChapter((c) => Math.max(0, c - 1))}
                className="inline-flex items-center gap-2 text-sm text-ink-2 transition-colors hover:text-saffron disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" /> <T>Previous</T>
              </button>
              <span className="text-xs text-ink-3">{chapter + 1} / {chapters.length}</span>
              <button
                data-testid="katha-next-chapter-btn"
                disabled={chapter === chapters.length - 1}
                onClick={() => setChapter((c) => Math.min(chapters.length - 1, c + 1))}
                className="inline-flex items-center gap-2 text-sm text-ink-2 transition-colors hover:text-saffron disabled:opacity-30"
              >
                <T>Next</T> <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          <SourcesBlock item={item} />
        </div>
      </div>

      {item.related_items?.length > 0 && (
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <Divider className="mb-10" />
          <h3 className="mb-8 font-serif-latin text-2xl text-ink">{t("related")}</h3>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {item.related_items.map((r) => (
              <KnowledgeCard key={r.slug} item={r} />
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <RelatedServices type={item.type} deity={item.deity} />
      </div>

      <PageEngage refType="content" refSlug={item.slug} refTitle={title} />

      <ReaderMode
        open={readerOpen}
        onClose={() => setReaderOpen(false)}
        title={`${title}${current ? " — " + chapterTitle : ""}`}
        verses={body ? [{ body, num: null }] : []}
        onPrev={() => setChapter((c) => Math.max(0, c - 1))}
        onNext={() => setChapter((c) => Math.min(chapters.length - 1, c + 1))}
        hasPrev={chapters.length > 1 && chapter > 0}
        hasNext={chapters.length > 1 && chapter < chapters.length - 1}
        pageLabel={chapters.length > 1 ? `${chapter + 1} / ${chapters.length}` : undefined}
      />
    </article>
  );
}
